import { forwardRef, useEffect } from 'react';

import type { Form } from '../fixtures/domain.js';
import { Composer, type ComposerHandle, type ComposerProps } from './Composer.js';
import { useComposerPickers, type PickerConvo } from './useComposerPickers.js';

type ConversationComposerProps = Omit<ComposerProps, 'onForm' | 'onPickJob'> & {
  convo: PickerConvo;
  formLibrary: { forms: Form[]; error: boolean };
  /** The open conversation; a change closes any open picker (navigating between chats). */
  conversationId: string;
  /** A broadcast channel takes neither a form nor a job. */
  broadcast?: boolean;
  /** Out to the Forms section; omitted where there is none (the widget). */
  onManageForms?: () => void;
  /** A failed picker send surfaces here. */
  onSendError?: (message: string) => void;
  /** Scope the picker modals to a floating panel (the widget) instead of the viewport. */
  contained?: boolean;
};

/**
 * The composer plus its `+` pickers as one unit, so `/chat` and the widget render the
 * same thing and a new picker action is added in one place rather than wired onto each
 * surface's `Composer` by hand — the drift that left the widget without Form or Job.
 */
export const ConversationComposer = forwardRef<ComposerHandle, ConversationComposerProps>(
  function ConversationComposer(
    {
      convo,
      formLibrary,
      conversationId,
      broadcast = false,
      onManageForms,
      onSendError,
      contained = false,
      ...composer
    },
    ref,
  ) {
    const { onForm, onPickJob, dialogs, reset } = useComposerPickers(convo, {
      formLibrary,
      onManageForms,
      onError: onSendError,
      contained,
    });
    // Close an open picker when the chat changes; deselecting unmounts this instead.
    useEffect(() => {
      reset();
    }, [conversationId, reset]);

    return (
      <>
        <Composer
          ref={ref}
          {...composer}
          onForm={broadcast ? undefined : onForm}
          onPickJob={broadcast ? undefined : onPickJob}
        />
        {dialogs}
      </>
    );
  },
);
